// version v_2.2.0
// PRODUCTION AUTH FIXED
// ✔ no login race conditions
// ✔ cookie-based auth stable
// ✔ no false login failures
// ✔ safe session restore
// ✔ clean interceptor lifecycle

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback
} from 'react';

import { useNavigate } from 'react-router-dom';
import { useCart } from './CartContext';
import API, { setupAxiosInterceptors } from './api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {

  const { resetCart } = useCart();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);

  const [authLoaded, setAuthLoaded] = useState(false);
  const [authInitializing, setAuthInitializing] = useState(true);

  // =====================================================
  // LOGOUT
  // =====================================================
  const logout = useCallback(async () => {
    try {
      await API.post('/api/users/logout');
    } catch (err) {
      // ignore
    }

    setUser(null);
    setShippingAddress(null);
    resetCart();

    navigate('/login', { replace: true });
  }, [navigate, resetCart]);

  // =====================================================
  // AXIOS INTERCEPTOR
  // =====================================================
  useEffect(() => {
    setupAxiosInterceptors(logout);
  }, [logout]);

  // =====================================================
  // FETCH PROFILE (SESSION RESTORE ONLY)
  // =====================================================
  const fetchProfile = async () => {
    try {
      const { data } = await API.get('/api/users/profile');
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  // =====================================================
  // SHIPPING ADDRESS
  // =====================================================
  const fetchShippingAddress = async () => {
    try {
      const { data } = await API.get('/api/users/shipping');
      setShippingAddress(data?.shippingAddress || null);
    } catch {
      setShippingAddress(null);
    }
  };

  // =====================================================
  // INITIAL SESSION RESTORE
  // =====================================================
  useEffect(() => {
    const init = async () => {
      setAuthInitializing(true);

      const profile = await fetchProfile();

      if (profile) {
        await fetchShippingAddress();
      }

      setAuthLoaded(true);
      setAuthInitializing(false);
    };

    init();
  }, []);

  // =====================================================
  // LOGIN (FIXED - NO PROFILE RACE CONDITION)
  // =====================================================
  const login = async (email, password) => {
    try {
      resetCart();

      // ✅ login response is the source of truth
      const { data } = await API.post('/api/users/login', {
        email,
        password
      });

      setUser(data);

      // optional but safe
      await fetchShippingAddress();

      return {
        success: true,
        user: data
      };

    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Login failed'
      };
    }
  };

  // =====================================================
  // REGISTER
  // =====================================================
  const register = async (data) => {
    try {
      await API.post('/api/users/register', data);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Registration failed'
      };
    }
  };

  // =====================================================
  // SHIPPING UPDATE
  // =====================================================
  const updateShippingAddress = async (newAddress) => {
    try {
      const { data } = await API.put(
        '/api/users/shipping',
        { shippingAddress: newAddress }
      );

      setShippingAddress(data?.shippingAddress || null);

      return { success: true };

    } catch {
      return { success: false };
    }
  };

  // =====================================================
  // CONTEXT VALUE
  // =====================================================
  const value = useMemo(() => ({
    user,
    shippingAddress,
    authLoaded,
    authInitializing,
    login,
    logout,
    register,
    updateShippingAddress
  }), [
    user,
    shippingAddress,
    authLoaded,
    authInitializing,
    login,
    logout
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);